import type { AxiosInstance } from "axios";
import type { QueryClient } from "@tanstack/react-query";
import type {
  EventIdPublic,
  EventParticipantIdPublic,
  MatchingPipelineRunIdPublic,
} from "@shared/schemas/branded-id.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { createMatchingEngineAdminApi } from "./matching-engine-admin.api";
import type {
  CancelRunResult,
  CancelRunVariables,
  TriggerPipelineResult,
  TriggerPipelineVariables,
} from "@shared/types/admin-snapshot.types";
import {
  MatchingEngineRunSnapshotSchema,
  type MatchingEngineRunSnapshot,
} from "@shared/schemas/run-snapshot.schemas";
import {
  MatchingEngineParticipantSnapshotSchema,
  type MatchingEngineParticipantSnapshot,
} from "@shared/schemas/participant-snapshot.schemas";
import {
  MatchingEngineCohortSnapshotSchema,
  type MatchingEngineCohortSnapshot,
} from "@shared/schemas/cohort-snapshot.schemas";
import {
  CompositionDescriptorsSchema,
  type CompositionDescriptors,
} from "@shared/schemas/composition-descriptors.schemas";

const SNAPSHOT_STALE_TIME_MS = 30_000;

const QUERY_KEYS = {
  // Outside the `eventBase` prefix on purpose: a run trigger invalidates that
  // prefix, and the descriptors must not refetch with it (see staleTime below).
  compositionDescriptors: (eventIdPublic: EventIdPublic) =>
    [
      "matching-engine-admin",
      "composition-descriptors",
      eventIdPublic,
    ] as const,
  // Prefix covering every query for an event (all run-scoped snapshots + all
  // participant snapshots); invalidate this to refresh the whole event view.
  eventBase: (eventIdPublic: EventIdPublic) =>
    ["matching-engine-admin", "event", eventIdPublic] as const,
  event: (
    eventIdPublic: EventIdPublic,
    runIdPublic: MatchingPipelineRunIdPublic | undefined,
  ) =>
    [
      "matching-engine-admin",
      "event",
      eventIdPublic,
      "snapshot",
      runIdPublic ?? "__latest__",
    ] as const,
  cohort: (
    eventIdPublic: EventIdPublic,
    runIdPublic: MatchingPipelineRunIdPublic,
  ) =>
    [
      "matching-engine-admin",
      "event",
      eventIdPublic,
      "run",
      runIdPublic,
      "cohort",
    ] as const,
  participant: (
    eventIdPublic: EventIdPublic,
    runIdPublic: MatchingPipelineRunIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ) =>
    [
      "matching-engine-admin",
      "event",
      eventIdPublic,
      "run",
      runIdPublic,
      "participant",
      participantIdPublic,
    ] as const,
};

export class MatchingEngineAdminHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createMatchingEngineAdminApi(apiClient);
  }

  createHooks() {
    const { api } = this;
    return {
      useGetRunSnapshot: (
        eventIdPublic: EventIdPublic,
        runIdPublic?: MatchingPipelineRunIdPublic,
      ) =>
        this.query<unknown, MatchingEngineRunSnapshot>({
          key: QUERY_KEYS.event(eventIdPublic, runIdPublic),
          fn: () => api.getEventSnapshot(eventIdPublic, runIdPublic),
          parse: (dto) => MatchingEngineRunSnapshotSchema.parse(dto),
          staleTime: SNAPSHOT_STALE_TIME_MS,
        }),

      useGetParticipantSnapshot: (
        eventIdPublic: EventIdPublic,
        runIdPublic: MatchingPipelineRunIdPublic,
        participantIdPublic: EventParticipantIdPublic,
      ) =>
        this.query<unknown, MatchingEngineParticipantSnapshot>({
          key: QUERY_KEYS.participant(
            eventIdPublic,
            runIdPublic,
            participantIdPublic,
          ),
          fn: () =>
            api.getParticipantSnapshot(
              eventIdPublic,
              runIdPublic,
              participantIdPublic,
            ),
          parse: (dto) => MatchingEngineParticipantSnapshotSchema.parse(dto),
          staleTime: SNAPSHOT_STALE_TIME_MS,
        }),

      useGetCohortSnapshot: (
        eventIdPublic: EventIdPublic,
        runIdPublic: MatchingPipelineRunIdPublic,
      ) =>
        this.query<unknown, MatchingEngineCohortSnapshot>({
          key: QUERY_KEYS.cohort(eventIdPublic, runIdPublic),
          fn: () => api.getCohortSnapshot(eventIdPublic, runIdPublic),
          parse: (dto) => MatchingEngineCohortSnapshotSchema.parse(dto),
          staleTime: SNAPSHOT_STALE_TIME_MS,
        }),

      useGetCompositionDescriptors: (eventIdPublic: EventIdPublic) =>
        this.query<unknown, CompositionDescriptors>({
          key: QUERY_KEYS.compositionDescriptors(eventIdPublic),
          fn: () => api.getCompositionDescriptors(eventIdPublic),
          parse: (dto) => CompositionDescriptorsSchema.parse(dto),
          // Descriptors are baked at deploy time; never auto-refetch.
          staleTime: Number.POSITIVE_INFINITY,
        }),

      useTriggerPipeline: () =>
        this.mutation<
          TriggerPipelineResult,
          TriggerPipelineResult,
          TriggerPipelineVariables
        >({
          fn: ({
            eventIdPublic,
            mode,
            targetStage,
            experiment,
            participantIdsPublic,
          }) =>
            this.api.triggerPipeline(
              eventIdPublic,
              mode,
              targetStage,
              experiment,
              participantIdsPublic,
            ),
          parse: (data) => data,
          onSuccess: (_data, { eventIdPublic }) => {
            this.invalidateQueries(QUERY_KEYS.eventBase(eventIdPublic));
          },
        }),

      useCancelRun: () =>
        this.mutation<CancelRunResult, CancelRunResult, CancelRunVariables>({
          fn: ({ eventIdPublic, runIdPublic }) =>
            this.api.cancelRun(eventIdPublic, runIdPublic),
          parse: (data) => data,
          onSuccess: (_data, { eventIdPublic }) => {
            this.invalidateQueries(QUERY_KEYS.eventBase(eventIdPublic));
          },
        }),
    };
  }
}
